import React, { useCallback, useMemo, useState } from 'react';
import VisuallyHidden from '@components/Atoms/VisuallyHidden/VisuallyHidden';
import { activeDayLevel, describeCell, sumCounts } from './intensity';
import { useTeacherSections } from './hooks';
import { formatBucket, formatBucketHeader, formatLastSeen } from './bucketFormat';
import { emptyRowAttr, hasActivity } from './rowFilters';
import {
  AccountabilityReport,
  Bucket,
  EmptyRowsMode,
  ReportQuery,
  ReportRow,
} from './types';
import MetricBands from './MetricBands';
import HeatCell from './HeatCell';
import {
  GridScroller,
  GridTable,
  RowHeadCell,
  BucketHeaderCell,
  TotalHeaderCell,
  TotalCell,
  ToggleButton,
  LastSeen,
  SectionTag,
} from './Accountability.style';

const DEPTH_BY_TYPE: Record<string, number> = {
  district: 0,
  school: 1,
  teacher: 2,
};
/** A teacher's activity rows and its "By section" row. */
const TEACHER_DETAIL_DEPTH = 3;
const SECTION_DEPTH = 4;
const SECTION_BANDS_DEPTH = 5;

export type ExpansionKind = 'rows' | 'sectionLists' | 'sectionBands';

/**
 * What is open in the grid. Transient view state owned by the page, never
 * written to the URL.
 */
export interface Expansion {
  /**
   * For district and school rows: their children are shown. For a teacher
   * row: the teacher's activity summary (and its "By section" row) is shown.
   */
  rows: string[];
  /** Teachers whose "By section" list is open. */
  sectionLists: string[];
  /** Sections whose own activity rows are shown. */
  sectionBands: string[];
}

interface GridProps {
  report: AccountabilityReport;
  query: ReportQuery;
  expansion: Expansion;
  onToggle: (kind: ExpansionKind, key: string) => void;
  /** Activity types nobody in the view used, when the toolbar hides them. */
  hiddenMetricSlugs: ReadonlySet<string>;
  /** "hide" also drops classes with no active day. */
  emptyRows: EmptyRowsMode;
  /** Admin override for the active-day denominator; null uses the bucket default. */
  activeDayTarget?: number | null;
}

/** What every heat strip in the grid needs to colour and describe its cells. */
interface StripContext {
  bucket: Bucket;
  bucketLabels: string[];
  activeDayTarget?: number | null;
  hoveredCol: number | null;
}

/**
 * Tag parts that tell copies of one class apart: the curriculum, or the course
 * name when there is none, and "archived".
 */
const sectionTagParts = (section: ReportRow): string[] => {
  const parts: string[] = [];
  const source = section.curriculum || section.course;
  if (source) {
    parts.push(source);
  }
  if (section.archived) {
    parts.push('archived');
  }
  return parts;
};

/** "1 day", "12 days". */
const formatDays = (days: number): string => `${days} ${days === 1 ? 'day' : 'days'}`;

/**
 * The Total and active-day cells of a district, school, teacher or section
 * row. The Total says "days" so it is not read as an activity count.
 */
const StripCells: React.FC<StripContext & { row: ReportRow; label?: string }> = ({
  row,
  label,
  bucket,
  bucketLabels,
  activeDayTarget,
  hoveredCol,
}) => (
  <>
    <TotalCell data-testid="strip-total">{formatDays(sumCounts(row.strip))}</TotalCell>
    {row.strip.map((count, index) => (
      <HeatCell
        key={`${row.key}-${index}`}
        colIndex={index}
        highlighted={hoveredCol === index}
        count={count}
        level={activeDayLevel(count, bucket, activeDayTarget)}
        label={describeCell({
          entityName: label ?? row.name,
          bucketLabel: bucketLabels[index],
          count,
          kind: 'activeDays',
          bucket,
          activeDayTarget,
        })}
      />
    ))}
  </>
);

interface TeacherDetailProps extends StripContext {
  query: ReportQuery;
  teacher: ReportRow;
  expansion: Expansion;
  onToggle: (kind: ExpansionKind, key: string) => void;
  hiddenMetricSlugs: ReadonlySet<string>;
  emptyRows: EmptyRowsMode;
}

/**
 * An open teacher: their activity rows, then a "By section" row that loads their
 * classes when opened. Classes with no active day are left out like teachers,
 * by "Active teachers" and by "Hide rows with no active days".
 */
const TeacherDetail: React.FC<TeacherDetailProps> = ({
  query,
  teacher,
  expansion,
  onToggle,
  hiddenMetricSlugs,
  emptyRows,
  ...strip
}) => {
  const sectionsOpen = expansion.sectionLists.includes(teacher.key);
  const { data, isLoading, error } = useTeacherSections(query, teacher.key, sectionsOpen);
  const showEmptyClasses = query.teachersMode === 'all' && emptyRows === 'show';
  const sections = data
    ? data.filter((row) => showEmptyClasses || hasActivity(row.strip))
    : null;
  const valueColumns = strip.bucketLabels.length + 1;

  const statusRow = (message: string) => (
    <tr>
      <RowHeadCell depth={SECTION_DEPTH}>{message}</RowHeadCell>
      <td colSpan={valueColumns} />
    </tr>
  );

  let sectionRows: React.ReactNode = null;
  if (sectionsOpen) {
    if (isLoading) {
      sectionRows = statusRow('Loading sections…');
    } else if (error || !sections) {
      // Distinct from an empty list: a failed fetch must not read as "this
      // teacher has no sections".
      sectionRows = statusRow('Could not load sections.');
    } else if (sections.length === 0) {
      sectionRows = statusRow('No sections with activity in this window.');
    } else {
      sectionRows = sections.map((section) => {
        const bandsOpen = expansion.sectionBands.includes(section.key);
        const tagParts = sectionTagParts(section);
        // Screen readers get the tag too, for example "Period 01, ACT".
        const accessibleName = [section.name, ...tagParts].join(', ');
        return (
          <React.Fragment key={section.key}>
            <tr data-empty={emptyRowAttr(section.strip)}>
              <RowHeadCell depth={SECTION_DEPTH}>
                <ToggleButton
                  type="button"
                  onClick={() => onToggle('sectionBands', section.key)}
                  aria-expanded={bandsOpen}
                  aria-label={
                    bandsOpen
                      ? `Hide metrics for ${accessibleName}`
                      : `Show metrics for ${accessibleName}`
                  }
                >
                  <span aria-hidden="true">{bandsOpen ? '▾' : '▸'}</span>
                  {section.name}
                  {tagParts.length > 0 && (
                    <SectionTag title={section.course ?? undefined}>
                      · {tagParts.join(' · ')}
                    </SectionTag>
                  )}
                </ToggleButton>
              </RowHeadCell>
              <StripCells row={section} label={accessibleName} {...strip} />
            </tr>
            {bandsOpen && (
              <MetricBands
                query={query}
                entityKey={section.key}
                entityName={accessibleName}
                depth={SECTION_BANDS_DEPTH}
                hiddenMetricSlugs={hiddenMetricSlugs}
                {...strip}
              />
            )}
          </React.Fragment>
        );
      });
    }
  }

  return (
    <>
      <MetricBands
        query={query}
        entityKey={teacher.key}
        entityName={teacher.name}
        depth={TEACHER_DETAIL_DEPTH}
        hiddenMetricSlugs={hiddenMetricSlugs}
        {...strip}
      />
      <tr>
        <RowHeadCell depth={TEACHER_DETAIL_DEPTH}>
          <ToggleButton
            type="button"
            onClick={() => onToggle('sectionLists', teacher.key)}
            aria-expanded={sectionsOpen}
          >
            <span aria-hidden="true">{sectionsOpen ? '▾' : '▸'}</span>
            <span>
              {sections ? `By section (${sections.length})` : 'By section'}
              <VisuallyHidden>{` for ${teacher.name}`}</VisuallyHidden>
            </span>
          </ToggleButton>
        </RowHeadCell>
        <td colSpan={valueColumns} />
      </tr>
      {sectionRows}
    </>
  );
};

const AccountabilityGrid: React.FC<GridProps> = ({
  report,
  query,
  expansion,
  onToggle,
  hiddenMetricSlugs,
  emptyRows,
  activeDayTarget,
}) => {
  const { bucket } = report.window;
  const bucketLabels = useMemo(
    () => report.buckets.map((iso) => formatBucket(iso, bucket)),
    [report.buckets, bucket],
  );

  const [hoveredCol, setHoveredCol] = useState<number | null>(null);

  /**
   * Single delegated handler on the <table>, not a per-cell onMouseEnter.
   * `mouseover` bubbles (unlike `mouseenter`), so one listener here catches
   * pointer entry into every cell without creating a closure per cell --
   * essential at ~1,000 rows x ~41 columns, where a fresh handler per cell
   * per render would defeat HeatCell's React.memo. `data-col` on the cell
   * is the only per-cell cost, and it is a plain DOM attribute, not a prop
   * that participates in the memo comparison of anything else. The Total
   * column carries no `data-col`, so hovering it leaves the highlight alone.
   */
  const handleColumnHover = useCallback((event: React.MouseEvent<HTMLTableElement>) => {
    const cell = (event.target as HTMLElement).closest<HTMLElement>('[data-col]');
    if (!cell) {
      return;
    }
    const col = Number(cell.dataset.col);
    setHoveredCol((prev) => (prev === col ? prev : col));
  }, []);

  /**
   * `mouseleave` (unlike `mouseover`) does not bubble, so attaching it
   * directly to the table only fires when the pointer leaves the table as
   * a whole -- moving between adjacent cells never clears `hoveredCol`
   * through `null` along the way.
   */
  const handleColumnLeave = useCallback(() => setHoveredCol(null), []);

  /*
   * Both isVisible and hasChildren used to re-derive a lookup structure from
   * `report.rows` on every call -- once PER ROW, on every render, including
   * every hoveredCol change from the column-hover handler above. At ~1,000
   * rows that is the actual O(n^2) cost previously (mis)attributed to CSS
   * restyle cost during hover. Building each lookup once per `report.rows`
   * identity, instead of once per row per render, turns the whole
   * `.filter(isVisible).map(...)` pass below into O(n) again.
   */
  const rowsByKey = useMemo(
    () => new Map(report.rows.map((r) => [r.key, r])),
    [report.rows],
  );

  const keysWithChildren = useMemo(() => {
    const parents = new Set<string>();
    report.rows.forEach((r) => {
      if (r.parent) {
        parents.add(r.parent);
      }
    });
    return parents;
  }, [report.rows]);

  const expandedRows = expansion.rows;

  const isVisible = useCallback(
    (row: ReportRow): boolean => {
      if (!row.parent) {
        return true;
      }
      // Walk up: every ancestor must be expanded.
      let parentKey: string | null = row.parent;
      while (parentKey) {
        if (!expandedRows.includes(parentKey)) {
          return false;
        }
        const parentRow: ReportRow | undefined = rowsByKey.get(parentKey);
        parentKey = parentRow ? parentRow.parent : null;
      }
      return true;
    },
    [expandedRows, rowsByKey],
  );

  const strip: StripContext = { bucket, bucketLabels, activeDayTarget, hoveredCol };

  return (
    <GridScroller
      tabIndex={0}
      role="region"
      aria-label="Accountability heatmap, scrollable horizontally"
    >
      <GridTable onMouseOver={handleColumnHover} onMouseLeave={handleColumnLeave}>
        <thead>
          <tr>
            <th scope="col" style={{ textAlign: 'left', paddingLeft: '0.75rem' }}>
              School / Teacher / Section
            </th>
            <TotalHeaderCell scope="col">Total</TotalHeaderCell>
            {report.buckets.map((iso, index) => (
              <BucketHeaderCell
                key={iso}
                scope="col"
                title={bucketLabels[index]}
                highlighted={hoveredCol === index}
                data-col={index}
                data-highlighted={hoveredCol === index ? 'true' : 'false'}
              >
                {formatBucketHeader(iso, bucket)}
              </BucketHeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.rows.filter(isVisible).map((row) => {
            const expanded = expandedRows.includes(row.key);
            const isTeacher = row.type === 'teacher';
            let label: React.ReactNode = row.name;
            if (isTeacher) {
              // A teacher's chevron opens their activity rows; sections come from inside.
              label = (
                <ToggleButton
                  type="button"
                  onClick={() => onToggle('rows', row.key)}
                  aria-expanded={expanded}
                  aria-label={
                    expanded
                      ? `Hide metrics for ${row.name}`
                      : `Show metrics for ${row.name}`
                  }
                >
                  <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
                  {row.name}
                </ToggleButton>
              );
            } else if (keysWithChildren.has(row.key)) {
              label = (
                <ToggleButton
                  type="button"
                  onClick={() => onToggle('rows', row.key)}
                  aria-expanded={expanded}
                  aria-label={expanded ? `Collapse ${row.name}` : `Expand ${row.name}`}
                >
                  <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
                  {row.name}
                </ToggleButton>
              );
            }

            return (
              <React.Fragment key={row.key}>
                <tr data-empty={emptyRowAttr(row.strip)}>
                  <RowHeadCell depth={DEPTH_BY_TYPE[row.type] ?? 0}>
                    {label}
                    {isTeacher && (
                      <LastSeen>last seen {formatLastSeen(row.last_seen)}</LastSeen>
                    )}
                  </RowHeadCell>
                  <StripCells row={row} {...strip} />
                </tr>
                {isTeacher && expanded && (
                  <TeacherDetail
                    query={query}
                    teacher={row}
                    expansion={expansion}
                    onToggle={onToggle}
                    hiddenMetricSlugs={hiddenMetricSlugs}
                    emptyRows={emptyRows}
                    {...strip}
                  />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </GridTable>
    </GridScroller>
  );
};

export default AccountabilityGrid;
